import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { outputFromObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, fromEvent, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  CORE_BACKGROUND_PLAYER_AD_COLOR,
  CORE_BACKGROUND_PLAYER_AD_URL,
  CORE_BACKGROUND_PLAYER_URL,
  CORE_DEBUG,
  CORE_STARTUP_ID,
} from '../../../core.tokens';
import {
  ClusterService,
  InteractionService,
  Intercom,
  NetworkService,
  RestaurantTableService,
  WatchdogService,
} from '../../services';
import { IRestaurantTable } from '../../models';

@Component({
  selector: 'core-background-player',
  standalone: true,
  templateUrl: './background-player.component.html',
  styleUrls: ['./background-player.component.scss'],
  host: {
    '[class.playing]': 'isPlaying()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackgroundPlayerComponent {

  private readonly id = inject(CORE_STARTUP_ID);
  private readonly debug = inject(CORE_DEBUG);
  private readonly backgroundPlayerUrl = inject(CORE_BACKGROUND_PLAYER_URL);
  private readonly backgroundPlayerAdUrl = inject(CORE_BACKGROUND_PLAYER_AD_URL);
  private readonly backgroundPlayerAdColor = inject(CORE_BACKGROUND_PLAYER_AD_COLOR);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly watchdog = inject(WatchdogService);
  private readonly network = inject(NetworkService);
  private readonly cluster = inject(ClusterService);
  private readonly intercom = inject(Intercom);
  private readonly restaurantTable = inject(RestaurantTableService);
  private readonly interaction = inject(InteractionService);


  private readonly logger = this.watchdog.tag('Background Player', 'lime');

  private readonly playing$ = new BehaviorSubject<boolean>(false);

  public readonly playing = outputFromObservable(this.playing$);

  public readonly iframeUrl = signal<SafeResourceUrl | null>(null);

  public readonly isPlaying = toSignal(this.playing$, { initialValue: false });

  private channelMessagesSubscription: Subscription | null = null;
  private intercomMessagesSubscription: Subscription | null = null;

  constructor() {
    this.network.status$.pipe(
      switchMap((status) => {
        if (!status) {
          return of(null);
        }

        return this.restaurantTable.table$.pipe(
          map((table) => this.prepareConfig(table)),
          map((config) => {
            if (config.providers.length > 0) {
              return btoa(unescape(encodeURIComponent(JSON.stringify(config))));
            }

            return null;
          }),
          map((config) => {
            if (this.backgroundPlayerAdUrl && config) {
              const url = new URL(this.backgroundPlayerAdUrl);
              url.searchParams.set('config', config);
              return url.toString();
            }

            return this.backgroundPlayerUrl;
          }),
        );
      }),
      distinctUntilChanged(),
      map((url) => {
        if (!url) {
          return null;
        }

        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((url) => {
      this.iframeUrl.set(url);
    });

    fromEvent(window, 'unload').pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.intercom.call('playlist.resume');
    });

    this.destroyRef.onDestroy(() => {
      this.intercom.call('playlist.resume');

      this.channelMessagesSubscription?.unsubscribe();
      this.intercomMessagesSubscription?.unsubscribe();
    });
  }

  public onLoadData(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    const channel = new MessageChannel();

    this.channelMessagesSubscription?.unsubscribe();
    this.intercomMessagesSubscription?.unsubscribe();

    iframe.contentWindow?.postMessage(
      'dwall-handshake', '*', [channel.port2],
    );

    this.channelMessagesSubscription = fromEvent<MessageEvent>(
      channel.port1, 'message',
    ).subscribe((messageEvent) => {
      const message = JSON.parse(messageEvent.data);

      if (message.method === 'interaction') {
        this.interaction.interact();
      }

      if (message.method === 'playlist.resume') {
        this.playing$.next(false);
      }

      if (message.method === 'playlist.stop') {
        this.playing$.next(true);
      }

      this.intercom.call(message.method, message.data);
    });

    this.intercomMessagesSubscription = this.intercom.messages$.subscribe((message) => {
      this.logger.debug('Sent', message);

      channel.port1.postMessage(JSON.stringify(message));
    });

    channel.port1.start();
  }

  public onError(event: Event): void {
    this.logger.error('Could not load background player', event);
    this.iframeUrl.set(null);
  }

  private prepareConfig(table: IRestaurantTable | null) {
    const config: {
      type: 'order' | 'time',
      every: number,
      bgColor: string,
      debug: boolean,
      providers: {
        id: string,
        [key: string]: unknown
      }[]
    } = {
      type: 'order',
      every: table?.adRunEvery ?? 3,
      bgColor: this.backgroundPlayerAdColor ?? '#000000',
      debug: this.debug,
      providers: [],
    };

    const leader = this.cluster.leader;
    const integrations = leader
      ? (table?.integrations ?? [])
      : (table?.sideCarAppsIntegrations[this.id] ?? []);

    if (integrations.length) {
      integrations.map(integration => {
        if (integration.provider === 'VAST') {
          config.providers.push({
            id: 'VAST',
            tagUrl: integration.credentials.vastTagUrl,
          });
        }

        if (integration.provider === 'trillboards') {
          config.providers.push({
            id: leader ? 'trillboards' : 'trillboards.mirror',
            fingerprint: integration.credentials.fingerprint,
          });
        }
      });
    }

    return config;
  }

}
